import { getProxyAuthHeaders } from '@/auth/imsTokenStorage';
import { config } from '@/config/env';
import { logger } from '@/logger';

// Everything we ask Meta for. Exported so a test can pin the list.
export const FACEBOOK_OAUTH_SCOPES: readonly string[] = [
	'instagram_basic',
	'instagram_manage_comments',
	'instagram_manage_insights',
	'instagram_manage_messages',
	'pages_show_list',
	'pages_read_engagement',
	'business_management',
	'pages_manage_metadata',
	'pages_messaging',
];

// The permissions Beacon cannot work without, which is a much shorter list than
// the one we request. Several requested names need Meta app review, and Meta
// only ever lists what it granted, so checking the full request list would warn
// a working customer about permissions Beacon never uses.
//
// This mirrors REQUIRED_INSTAGRAM_SYNC_SCOPES in the beacon repository, in
// shared/utils/instagram_client.py. The two repositories cannot share a module,
// so the two lists must be kept equal by hand. A test on each side pins its own
// copy so a silent drift fails loudly.
export const REQUIRED_INSTAGRAM_SCOPES: readonly string[] = [
	'instagram_manage_insights',
];

const FACEBOOK_OAUTH_SCOPE_PARAM = FACEBOOK_OAUTH_SCOPES.join(',');

// LocalStorage key for Facebook Meta data
const FACEBOOK_META_STORAGE_KEY = 'facebookMeta';

/**
 * Error thrown when a Facebook call fails. Carries which call failed and the
 * error fields Meta returned, so a failed connect can be reported with the
 * concrete blocker instead of a bare sentence. Every Meta field is optional:
 * a call that never reached Meta has only a `status`.
 */
export class FacebookApiError extends Error {
	readonly step: 'token_exchange' | 'fetch_pages';
	readonly status: number;
	readonly metaCode?: number;
	readonly metaSubcode?: number;
	readonly metaType?: string;
	readonly fbtraceId?: string;
	readonly metaMessage?: string;

	constructor(
		message: string,
		fields: {
			step: 'token_exchange' | 'fetch_pages';
			status: number;
			metaCode?: number;
			metaSubcode?: number;
			metaType?: string;
			fbtraceId?: string;
			metaMessage?: string;
		},
	) {
		super(message);
		this.name = 'FacebookApiError';
		this.step = fields.step;
		this.status = fields.status;
		this.metaCode = fields.metaCode;
		this.metaSubcode = fields.metaSubcode;
		this.metaType = fields.metaType;
		this.fbtraceId = fields.fbtraceId;
		this.metaMessage = fields.metaMessage;
	}
}

export interface FacebookMetaData {
	page_id: string;
	user_token: {
		access_token: string;
		expires_at: string; // ISO date string
	};
	page_token: {
		access_token: string;
		expires_at: string; // ISO date string
	};
	instagram_id: string;
	instagram_handle: string;
}

export interface ConnectedInstagramAccount {
	id: string;
	name: string;
	username: string;
	biography?: string;
	profile_picture_url?: string;
	followers_count?: number;
	follows_count?: number;
	media_count?: number;
	website?: string;
}

export interface FacebookPage {
	id: string;
	name: string;
	access_token: string;
	connected_instagram_account?: ConnectedInstagramAccount;
	instagram_business_account?: {
		id: string;
	};
	picture?: {
		data: {
			url: string;
			width: number;
			height: number;
			is_silhouette: boolean;
		};
	};
}

export interface FacebookAccountsResponse {
	data: FacebookPage[];
	paging?: {
		cursors: {
			before: string;
			after: string;
		};
	};
}

export interface LongLivedTokenResponse {
	access_token: string;
	token_type: string;
	expires_in?: number; // seconds until expiration — may be absent for never-expiring page tokens
	expires_at?: number; // Unix timestamp from debug_token; 0 means never-expiring page token
	data_access_expires_at?: number; // Unix timestamp (~90 days from grant); gates actual data reads
}

export const getInstagramAuthRedirectUri = (): string => {
	return `${window.location.origin}/auth/instagram`;
};

export const redirectToFacebookOAuth = (): void => {
	const oauthEndpoint = 'https://www.facebook.com/v24.0/dialog/oauth';
	const redirectUri = getInstagramAuthRedirectUri();

	const oauthParams = new URLSearchParams({
		client_id: config.META_APP_CLIENT_ID,
		display: 'page',
		redirect_uri: redirectUri,
		// Meta answers with a granted_scopes list only when we ask for it. The
		// customer can clear individual permissions on the Facebook screen, so
		// the request list is not proof of what we actually hold.
		//
		// On this dialog URL the ask is a response_type value, not a separate
		// parameter. return_scopes is the FB.login() JavaScript SDK option, and
		// this code builds the redirect by hand, so the dialog drops it and
		// answers with no granted_scopes at all.
		response_type: 'token,granted_scopes',
		scope: FACEBOOK_OAUTH_SCOPE_PARAM,
	});

	const oauthUrl = `${oauthEndpoint}?${oauthParams.toString()}`;
	logger.info(`Redirecting to Facebook OAuth: ${oauthUrl}`);

	window.location.href = oauthUrl;
};

// `grantedScopes` is null when Meta sent no granted_scopes value at all, which
// means the grant is unknown. An empty array is a different answer: Meta replied
// and granted nothing. Never collapse the two.
export const extractAccessTokenFromHash = (): {
	accessToken: string | null;
	expiresIn: number | null;
	error: string | null;
	grantedScopes: string[] | null;
} => {
	// Facebook's implicit flow returns a permission denial in the query string
	// (?error=access_denied&...), while a successful grant comes back in the hash
	// fragment. Check the query string first so a denial isn't lost when the hash
	// is empty.
	const queryParams = new URLSearchParams(window.location.search);
	const queryError = queryParams.get('error');
	if (queryError) {
		const errorMessage =
			queryError === 'access_denied'
				? // Facebook's own error_description for a denial is the unhelpful
					// "Permissions error", so substitute copy the user can act on.
					"Instagram connection was cancelled or the permissions weren't granted. To connect, approve all the requested permissions on the Facebook screen."
				: queryParams.get('error_description') || queryError;
		return {
			accessToken: null,
			expiresIn: null,
			error: errorMessage,
			grantedScopes: null,
		};
	}

	const hash = window.location.hash;

	if (!hash || hash.length <= 1) {
		return {
			accessToken: null,
			expiresIn: null,
			error: null,
			grantedScopes: null,
		};
	}

	// Remove the leading '#' and parse as URLSearchParams
	const params = new URLSearchParams(hash.substring(1));

	// Check for error response
	const error = params.get('error');
	if (error) {
		const errorDescription = params.get('error_description') || error;
		return {
			accessToken: null,
			expiresIn: null,
			error: errorDescription,
			grantedScopes: null,
		};
	}

	const accessToken = params.get('access_token');
	const expiresInStr = params.get('expires_in');
	const expiresIn = expiresInStr ? parseInt(expiresInStr, 10) : null;

	const grantedScopesParam = params.get('granted_scopes');
	const grantedScopes =
		grantedScopesParam === null
			? null
			: grantedScopesParam
					.split(',')
					.map((scope) => scope.trim())
					.filter((scope) => scope.length > 0);

	return { accessToken, expiresIn, error: null, grantedScopes };
};

export const clearUrlHash = (): void => {
	window.history.replaceState({}, '', window.location.pathname);
};

export const fetchFacebookAccounts = async (
	userToken: string,
): Promise<FacebookAccountsResponse> => {
	const fields = [
		'access_token',
		'connected_instagram_account{id,name,username,biography,profile_picture_url,followers_count,follows_count,media_count,website}',
		'instagram_business_account',
		'name',
		'picture',
	].join(',');

	const params = new URLSearchParams({
		fields,
		access_token: userToken,
	});

	const response = await fetch(
		`https://graph.facebook.com/v24.0/me/accounts?${params.toString()}`,
	);

	if (!response.ok) {
		const errorData = await response.json();
		logger.error('Failed to fetch Facebook accounts:', errorData);
		// This call goes straight to graph.facebook.com, so the body is Meta's own.
		const metaError = errorData?.error;
		throw new FacebookApiError(
			metaError?.message || 'Failed to fetch Facebook accounts',
			{
				step: 'fetch_pages',
				status: response.status,
				metaCode: metaError?.code,
				metaSubcode: metaError?.error_subcode,
				metaType: metaError?.type,
				fbtraceId: metaError?.fbtrace_id,
				metaMessage: metaError?.message,
			},
		);
	}

	return response.json();
};

export const exchangeForLongLivedToken = async (
	shortLivedToken: string,
): Promise<LongLivedTokenResponse> => {
	const response = await fetch(
		`${config.UI_PROXY_URL}/facebook-proxy/exchange-token`,
		{
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				...getProxyAuthHeaders(),
			},
			body: JSON.stringify({ accessToken: shortLivedToken }),
		},
	);

	if (!response.ok) {
		const errorData = await response.json();
		logger.error('Failed to exchange for long-lived token:', errorData);
		// This call goes to the ui-proxy, where `error` is a plain sentence and
		// Meta's own fields arrive under `meta`. An older proxy sends no `meta`
		// at all, and a proxy failure before the Meta call has none to send, so
		// treat every Meta field as optional.
		const metaError = errorData?.meta;
		throw new FacebookApiError(
			errorData?.error || 'Failed to exchange for long-lived token',
			{
				step: 'token_exchange',
				status: response.status,
				metaCode: metaError?.code,
				metaSubcode: metaError?.error_subcode,
				metaType: metaError?.type,
				fbtraceId: metaError?.fbtrace_id,
				metaMessage: metaError?.message,
			},
		);
	}

	return response.json();
};

export const saveFacebookMeta = (data: FacebookMetaData): void => {
	localStorage.setItem(FACEBOOK_META_STORAGE_KEY, JSON.stringify(data));
	logger.info('Saved Facebook Meta data to localStorage');
};

export const loadFacebookMeta = (): FacebookMetaData | null => {
	const stored = localStorage.getItem(FACEBOOK_META_STORAGE_KEY);
	if (!stored) return null;

	try {
		const data = JSON.parse(stored) as FacebookMetaData;
		const now = new Date();

		// Check if user token is expired (treat invalid dates as expired)
		const userTokenExpiresAt = new Date(data.user_token.expires_at);
		if (isNaN(userTokenExpiresAt.getTime()) || userTokenExpiresAt <= now) {
			logger.info('Facebook user token has expired or has invalid expiry');
			clearFacebookMeta();
			return null;
		}

		// Check if page token is expired (treat invalid dates as expired)
		const pageTokenExpiresAt = new Date(data.page_token.expires_at);
		if (isNaN(pageTokenExpiresAt.getTime()) || pageTokenExpiresAt <= now) {
			logger.info('Facebook page token has expired or has invalid expiry');
			clearFacebookMeta();
			return null;
		}

		return data;
	} catch {
		logger.error('Failed to parse Facebook Meta data from localStorage');
		clearFacebookMeta();
		return null;
	}
};

export const clearFacebookMeta = (): void => {
	localStorage.removeItem(FACEBOOK_META_STORAGE_KEY);
	logger.info('Cleared Facebook Meta data from localStorage');
};

/**
 * Calculate an ISO expiration timestamp from a duration in seconds.
 * If `expiresInSeconds` is undefined, defaults to 89 days from now
 * (Meta long-lived user tokens last ~60 days).
 */
export const calculateExpiresAt = (expiresInSeconds?: number): string => {
	const EIGHTY_NINE_DAYS_IN_SECONDS = 89 * 24 * 60 * 60;
	const seconds = expiresInSeconds ?? EIGHTY_NINE_DAYS_IN_SECONDS;
	const expiresAt = new Date(Date.now() + seconds * 1000);
	return expiresAt.toISOString();
};

export const hasInstagramAccountSelected = (): boolean => {
	const meta = loadFacebookMeta();
	return meta !== null && !!meta.instagram_id && !!meta.instagram_handle;
};
